import React, { useCallback, useEffect, useState } from 'react';
import './HelpCardEditor.css';
import SlateEditor from './SlateEditor';
import { themeNames } from '@fluentui/react-teams';
import { htmlParser, markdownToSlate } from './slate-editor-helpers';
import { 
  Editor,
  Transforms,
  Element as SlateElement,
  Node as SlateNode,
  Text as SlateText,
  Path,
  BasePoint, Range
 } from 'slate';
import { Alert, Button, Dialog, Dropdown, DropdownItemProps, ExclamationTriangleIcon, Flex, Input, MenuButton, Popup, Text } from '@fluentui/react-northstar';
import { isValidURL, toTitleCase } from '../../utils/helper';
import { BUTTONS, CustomHelpCardConstants, FILTERS_STRINGS, LICENSE_FEATURE } from '../../utils/constants';
import { ScopePermissionPicker } from '../../../Rbac/ScopePermissionPicker';
import { getBackgroundColor, LabelElement } from '../TicketForm';
import { LoadingScreen } from '../LoadingScreen';
import { useTranslation } from 'react-i18next';
import { CustomButtonData, CustomButtonProps, ScopingDetailsData } from './HelpCardAddButton';
import { appState, KBData, useSetState } from '../../../AppState';
import { platformService } from '../../services/platform.service';
import { getCachedFeature } from '../../cache/FeatureCache';
import { KB_Template_Filter } from '../../../kb/KBMain';
import { kbDataService } from '../../services/kb.service';
import { SortWithDefaultTemplateFirst } from '../../../templates/TicketTemplatesTable';
import AddButtonSVG from '../../../../svg/addButton.svg';
import ScopeButtonLightSVG from '../../../../svg/ScopeIcons/button/light.svg';
import ScopeButtonDarkSVG from '../../../../svg/ScopeIcons/button/dark.svg';
import { clone } from 'lodash';
import ResetDefaultSVG from '../../../../svg/resetDefault.svg';
import { CheckFeature } from '../CheckFeature';
import { minifyHtmlString } from '../../common/CommonHelper';
import { HistoryEditor } from 'slate-history';
import { ThemeColorScheme } from '../../common/TeamsTheme';


export const HelpCardSlateEditor = (props) => {

  const {
    globalTheme,
    editorState,
    handleCustomEditCancel,
    scopedButtons,
    handleScopedData
  }: CustomButtonProps = props;

  const menuItemsAll = [
      {
        key: CustomHelpCardConstants.Button_KnowledgeArticle,
        content: CustomHelpCardConstants.Button_KnowledgeArticle_Display,
        value: CustomHelpCardConstants.Button_KnowledgeArticle,
        description: CustomHelpCardConstants.Button_KnowledgeArticle_Description,
        ScopingDetails: {
          ScopedMembers:[]
        }
      },
      {
        key: CustomHelpCardConstants.Button_NewTicket,
        content: CustomHelpCardConstants.Button_NewTicket_Display,
        value: CustomHelpCardConstants.Button_NewTicket,
        description: CustomHelpCardConstants.Button_NewTicket_Description,
        ScopingDetails: {
          ScopedMembers:[]
        }
      },
      {
        key: CustomHelpCardConstants.Button_MyRequests,
        content: CustomHelpCardConstants.Button_MyRequests_Display,
        value: CustomHelpCardConstants.Button_MyRequests,
        description: CustomHelpCardConstants.Button_MyRequests_Description,
        ScopingDetails: {
          ScopedMembers:[]
        }
      },
      {
        key: CustomHelpCardConstants.Button_Template,
        content: CustomHelpCardConstants.Button_Template_Display,
        value: CustomHelpCardConstants.Button_Template,
        description: CustomHelpCardConstants.Button_Template_Description,
        ScopingDetails: {
          ScopedMembers:[]
        }
      },
      {
        key: CustomHelpCardConstants.Button_OpenURL,
        content: CustomHelpCardConstants.Button_OpenURL_Display,
        value: CustomHelpCardConstants.Button_OpenURL,
        description: CustomHelpCardConstants.Button_OpenURL_Description,
        ScopingDetails: {
          ScopedMembers:[]
        }
      },
      {
        key: CustomHelpCardConstants.Scoped_Text,
        content: CustomHelpCardConstants.Text,
        value: CustomHelpCardConstants.Scoped_Text,
        description: CustomHelpCardConstants.ScopedSelectedText,
        ScopingDetails: {
          ScopedMembers:[]
        }
      }
    ];

    const customButtomallowedKeys = [
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 
      'Home', 'End', 'PageUp', 'PageDown'
    ];
    
    const setState = useSetState();
    const currentState = appState();
    const api = new platformService();
    const [isOpen, setIsOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [buttonName, setButtonName] = useState("");
    const [buttonData, setButtonData] = useState("");
    const [buttonScopedData, setButtonScopedData] = useState<ScopingDetailsData>({ScopedMembers:[]});
    const [scopingEnabled, setScopingEnabled] = useState(false);
    const [buttonDataId, setButtonDataId] = useState("");
    const [buttonInvalid, setButtonInvalid] = useState(false);
    const [templates, setTemplates] = useState<Array<DropdownItemProps>>([]);
    const [kbList, setKBList] = useState<Array<DropdownItemProps>>([]);
    const [kbListFullProps, setKBListFullProps] = useState<Array<KBData>>([]);
    const [teams, setTeams] = useState<Array<DropdownItemProps>>([]);
    const [defaultTeam, setDefaulTeam] = useState(currentState?.defaultTeam?.Id ?? null);
    const [enabledMultiDepartment, setEnabledMultiDepartment] = useState(false);
    const [enabledScoping, setEnabledScoping] = useState(true);
    const [menuItems, setMenuItems] = useState(menuItemsAll);
    const [slateSelection, setSlateSelection] = useState<BasePoint>(null);
    const [lastButtonPath, setLastButtonPath] = useState<Path | null>(null);
    const [openScopedWindow, setOpenScopedWindow] = useState(false);
    const [newSelectedTextId, setNewSelectedTextId] = useState(0);
    const [isNewScopedText, setIsNewScopedText] = useState(false);
    const [openImgTool, setOpenImgTool] = useState(false);
    const scopeText = "scoped-text";
    const { t } = useTranslation();

    let editor: any = null;
    const [editData,setEditData] = useState<CustomButtonData>(null);

    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [editorIsLoaded, setEditorIsLoaded] = useState(false);


    const fetchButtonData = async () => {
      try {
        if(!isDataFetched)
          setIsLoading(true);
        const getTeamsFromState = () => currentState.teams;
        const getTeamsFromAPI = async () => (await api.getTeamsSafe(FILTERS_STRINGS.TICKET_TEAMS));
        const results = await Promise.allSettled([
          api.getTicketTemplates(KB_Template_Filter),
          (currentState?.teams.length > 0) ? Promise.resolve(getTeamsFromState()) : getTeamsFromAPI(),
          (currentState?.defaultTeam?.Id) ? [currentState?.defaultTeam] : api.getTeamsSafe(FILTERS_STRINGS.IS_DEFAULT),
          kbDataService.fetchAPI(),
          getCachedFeature(LICENSE_FEATURE.MultiDepartment),
          getCachedFeature(LICENSE_FEATURE.TicketDeflection),
          getCachedFeature(LICENSE_FEATURE.TemplateDeflection),
          getCachedFeature(LICENSE_FEATURE.PermissionScoping)
        ]);

        const thisEnabledMultiDepartment = (results[4].status == "fulfilled") ? results[4].value : false;
        setEnabledMultiDepartment(thisEnabledMultiDepartment);
        const thisEnabledTicketDeflection = (results[5].status == "fulfilled") ? results[5].value : false;
        const thisEnabledTemplateDeflection = (results[6].status == "fulfilled") ? results[6].value : false;
        setEnabledScoping((results[7].status == "fulfilled") ? results[7].value : false);
        setMenuItems(prev => {
          let items = prev;
          if(!thisEnabledTemplateDeflection)
            items = items.filter(i => i.key != CustomHelpCardConstants.Button_Template);
          if(!thisEnabledTicketDeflection)
            items = items.filter(i => i.key != CustomHelpCardConstants.Button_KnowledgeArticle);
          return items;
        });
        
        if(results[3].status == "fulfilled"){
          const kbValues = JSON.parse(results[3].value.data.value);
          const kbDropdownValues = kbValues.filter(x=>x.status=="Active").map(kb => {
            return getDropdownItems(kb.id, kb.questions[0]);
          });
          setKBList(kbDropdownValues);
          setKBListFullProps(kbValues);
        }
  
        if(results[0].status == "fulfilled"){
          const templateResults = SortWithDefaultTemplateFirst(results[0].value.data.value);
          const templates = templateResults.map(item => {
            return getDropdownItems(item.Id, item.Name);
          });
          setTemplates(templates);
        }

        if(results[1].status == "fulfilled" && results[2].status == "fulfilled"){
  
          const allTeams = results[1].value;
          const defaultTeam = results[2].value[0];
    
          setState(prevState => {
            prevState.teams = allTeams
            prevState.defaultTeam = defaultTeam
            return prevState;
          });
    
          const teams = allTeams.map(item => {
            return getDropdownItems(item.Id, item.FriendlyName);
          });
          setTeams(teams);
    
          setDefaulTeam(defaultTeam?.Id ?? null);
        }
        
        setIsDataFetched(true);
        setIsLoading(false);
        setLoaded(true);
        return true;
      } catch {
        console.log("Error fetching items...");
        setIsDataFetched(false);
        setIsLoading(false);
        return false;
      }
    }

    const getDropdownItems = (itemId, itemValue) => {
          return {
            accessibilityItemProps: {
              id: itemId,
              answerValue: itemValue
            },
            header: itemValue,
            selected: false
          } as DropdownItemProps;
        }

    useEffect(() => {
      fetchButtonData();
    }, [])

    useEffect(() => {
      if (openScopedWindow)
        setSelectedMenuIndex(5);
    }, [openScopedWindow])

    

    useEffect(() => {
      
      setButtonInvalid(false);
      const fetchData = async () => {
        const index = menuItems.findIndex(m => m.value == editData.Button);
        if(index == -1)
          handleCustomEditCancel(editorState);
        else {
          setSelectedMenuIndex(index);

          setButtonName(editData.DisplayName);
          setButtonData(editData.Data);
          setButtonDataId(editData.DataId);

          if (menuItems[index].key == CustomHelpCardConstants.Button_KnowledgeArticle)
            setButtonInvalid(!kbList.some(x => x.accessibilityItemProps.id == editData.DataId));

          if (menuItems[index].key == CustomHelpCardConstants.Button_Template)
            setButtonInvalid(!templates.some(x => x.accessibilityItemProps.id == editData.DataId));
          
          if(menuItems[index].key == CustomHelpCardConstants.Button_Template 
            || menuItems[index].key == CustomHelpCardConstants.Button_KnowledgeArticle 
            || menuItems[index].key == CustomHelpCardConstants.Button_NewTicket)
            setSearchText(editData.Data);
    
          setIsEdit(openEditDialog);
          setIsOpen(openEditDialog);
          const done = isDataFetched || await fetchButtonData();
          if(!done)
            setIsOpen(false);
        }
      }

      if(openEditDialog && !isLoading)
        fetchData().catch(console.error);
    }, [openEditDialog, isLoading])
    
    const handleClickedMentions = (e) => {
      const target = e.target as HTMLElement;
      if(target.classList.contains("deleteCustomButton")) return false;
      let anchorElement: HTMLAnchorElement;
      if(target.nodeName.toString() == "A")
        anchorElement = target as HTMLAnchorElement;
      else
      {
        const parentLink = target.closest(".wysiwyg-mention");
        if(parentLink)
          anchorElement = parentLink as HTMLAnchorElement;
      }
      
      const dataValue = JSON.parse(decodeURI(anchorElement.getAttribute("href")));
      props.clickedElement = anchorElement;
      props.editData = dataValue;
      setEditData(dataValue);
      setButtonName(dataValue.DisplayName);
      setOpenEditDialog(true);
    }

    const onChangeShowEditorCallback = useCallback((data: any, _editor:any, setEditorIsLoaded, editorIsLoaded:boolean) => {
      let text = ""
      editor = _editor;
      data.filter(x=>x?.children?.length==0).forEach((d) => {
          text += d.children[0]['text'] ?? ''
          if (typeof d.type != "undefined")
          {
            if (d.type == 'image') {
              text += 'image'
            }

            if (d.type == 'mention') {
              text += 'mention'
            }

            if (d.type == 'bulleted-list' || d.type ==  'numbered-list') {
              if (d.children[0].children[0]['text'])
                text += 'list'
            }
          }
      })
      
      
    const buttonLinks = document.querySelectorAll('#slate a[href^="%7B%22Button%22:"]');
    buttonLinks.forEach((link) => {
      link.removeEventListener('click', handleClickedMentions);
      link.addEventListener('click', handleClickedMentions);

      const span = link.querySelector('.scopedCustomButton');
      if (span) {
          span.addEventListener('click', event => {
              event.preventDefault();
              handleClickedMentions(event);
          });
      }

      const deleteBtn = link.querySelector('.deleteCustomButton');
      if (deleteBtn) {
          deleteBtn.addEventListener('click', event => {
              event.preventDefault();
              deleteButton(link.getAttribute("data-value"));
          });
      }
    });

    const scopedTextEditBtn = document.querySelectorAll('#slate .scope-text-container .editCustomButton');
    scopedTextEditBtn.forEach((btn)=>{
      btn.removeEventListener('click', editTextScoped);
      btn.addEventListener('click', editTextScoped);
    });

      let mk = htmlParser(data);
      props.helpcardProps.onChange(mk);
      if (!editorIsLoaded){
        setEditorIsLoaded(pre => true);
        editor.history = {
          undos: [],
          redos: [],
        };
      }
    }, []);

    const handleEditorKeyDown = (e,_editor) => {
      const { selection } = _editor;
      if (e.code === "Space" && e.shiftKey) {
        exitScopedGroup(editor,e, false);
      }
      else{
        if (selection){
          const [link] = Editor.nodes(editor, {
              match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link' && String(n.url || '').startsWith('%7B%22Button%22:'),
            });
            if (link){
              if (e.key === 'Backspace'){
                  const _link:any = link[0]
                  deleteButton(_link.url);
              }
              else if (!customButtomallowedKeys.includes(e.key))
                  e.preventDefault();
            }
        }
        
      }

    };
    

    const exitScopedGroup = (editor: Editor, event: KeyboardEvent, isRetry: boolean) => {
      try
      {
        const { selection } = editor;

        if (!selection || !Range.isCollapsed(selection)) return;

        const [current] = Editor.nodes(editor, {
          match: n => SlateText.isText(n) && (n as any).scoped?.id,
        });

        if (!current) return;

        const scopedId = (current[0] as any).scoped.id;
        // 1. get ALL scoped nodes with same id
        const scopedNodes = Array.from(
          Editor.nodes(editor, {
            at: [],
            match: n =>
              SlateText.isText(n) &&
              (n as any).scoped?.id === scopedId,
            mode: "all",
          })
        ) as Array<[any, Path]>;

        if (!scopedNodes.length) return;

        event.preventDefault();

        // 2. sort in document order
        const sorted = scopedNodes.sort((a, b) =>
          Path.compare(a[1], b[1])
        );

        // 3. get LAST scoped node
        const [, lastPath] = sorted[sorted.length - 1];

        // 4. move cursor AFTER it
        const exitPoint = Editor.end(editor, lastPath);

        Transforms.select(editor, exitPoint);
        
        const nextPath = Path.next(lastPath);

        if (nextPath) {
          Transforms.insertNodes(
            editor,
            {
              text: '',
              scoped: undefined,
            },
            {
              at: nextPath,
            }
          );

          // 3. ensure it's NOT scoped
          Transforms.setNodes(
            editor,
            { scoped: undefined },
            {
              at: nextPath,
              match: n => SlateText.isText(n),
            }
          );

          // 4. move cursor there
          Transforms.select(editor, Editor.start(editor, nextPath));
        }
      }
      catch {
        if (!isRetry) exitScopedGroup(editor,event, true);
      }
    }

    const deleteButton = (url) => {
      Transforms.removeNodes(editor, {
        match: n => {
          if (SlateElement.isElement(n) && n.type === 'link')
          {
            if (n.url==url)
              return true;
          }
          return false;
        },
        at: []
      });
    }

    const getDropdownElement = (label, items, isRequired: boolean = true) => {
          if (label == CustomHelpCardConstants.Knowledge) {
            items.find(x => true);
          }
    
          return <>
          <LabelElement label={label} required={isRequired} />
          <div className="teams-input-med">
            <Dropdown fluid search items={items} inverted clearable
            searchQuery={searchText}
            onSearchQueryChange={(_e, data) => {
              setSearchText(data.searchQuery);
            }}
            onChange={(e, p) => {
      
              if (!p.value && setSearchText)
                setSearchText("");
      
              const answer = p.value ? p.value['accessibilityItemProps'].answerValue : '';
              const answerId = p.value ? p.value['accessibilityItemProps'].id : 0;
    
              setButtonData(answer);
              setButtonDataId(`${answerId}`);
            }} 
            noResultsMessage = {<div>{CustomHelpCardConstants.No_Item}</div>}
            style={{ backgroundColor: getBackgroundColor(globalTheme) }}
            {...(buttonData?.length > 0 && { value: buttonData })}
            />
          </div>
          </>
        }

        const descriptionSection = <>
              <Flex className='mb-3'>
                <Text content={menuItems[selectedMenuIndex].description} /> 
              </Flex>
            </>;
        
        const nameSection = <>
              <LabelElement label={CustomHelpCardConstants.Name_Label} required />
              <div className="teams-input-med">
                <Input
                  onChange={e => {
                    const val = (e.target as HTMLInputElement).value;
                    setButtonName(val);
                  }}
                  fluid
                  value={buttonName}
                  id="editor-custom-focus-input"
                />
              </div>
            </>;

    const getDialogContent = (isScopedText:boolean) => {
          const selectedMenu = menuItems[selectedMenuIndex];
          let extras = <></>;
          switch(selectedMenu.key){
            case CustomHelpCardConstants.Button_Template:
              extras = getDropdownElement(CustomHelpCardConstants.Template, templates);
              break;
            case CustomHelpCardConstants.Button_NewTicket:
              if(enabledMultiDepartment)
                extras = getDropdownElement(CustomHelpCardConstants.Team, teams, false);
              break;
            case CustomHelpCardConstants.Button_KnowledgeArticle:
              extras = getDropdownElement(CustomHelpCardConstants.Knowledge, kbList);
              break;
            case CustomHelpCardConstants.Button_OpenURL:
              extras = <>
                <LabelElement label={'Link'} required />
                <div className="teams-input-med">
                  <Input
                    onChange={e => {
                      const val = (e.target as HTMLInputElement).value;
                      setButtonData(val);
                    }}
                    fluid
                    value={buttonData}
                  />
                </div>
              </>;
              break;
            default:
              break;
          }
          
          let scopedBtn = scopedButtons.find(x=>x.ButtonId==editData?.ButtonId);
          if (isEdit && scopedBtn)
          {
            selectedMenu.ScopingDetails=scopedBtn.ScopingDetails;
          }
          else
          {
            selectedMenu.ScopingDetails= {ScopedMembers:[]};
          }
      
          return (isLoading? <LoadingScreen message={CustomHelpCardConstants.Loading} height='50%' /> : <>
            {buttonInvalid && <Alert icon={<ExclamationTriangleIcon />} danger content={t('bot-configuration.help-card.catalogItemMissing')} />}
            {!isScopedText && <>
                {descriptionSection}
                {nameSection}
                {extras}
              </>
            }
            <>
              <CheckFeature featureName={LICENSE_FEATURE.PermissionScoping}>
                <LabelElement label={t('scoping.available-to')} />
                <div>
                  <ScopePermissionPicker
                    enabled={isNewScopedText || selectedMenu.ScopingDetails.ScopedMembers.length>0}
                    data={selectedMenu.ScopingDetails}
                    description={isScopedText ? t('scoping.description-bot-text') : t('scoping.description-bot-buttons')}
                    changed={(members,isEnabled)=>{
                      setScopingEnabled(isEnabled);
                      setButtonScopedData({ScopedMembers: members});
                    }}
                  />
                </div>
              </CheckFeature>

              
            </>
            
          </>);
        }

    const onMenuChange = async(e, data) =>{
      setSelectedMenuIndex(data.index);
      const selectedMenu = menuItems[data.index];
      setIsEdit(false);
      setIsOpen(true);
      const done = isDataFetched || await fetchButtonData();
      if(done && selectedMenu.key == CustomHelpCardConstants.Button_NewTicket&& teams?.length > 0 && defaultTeam){
        const val = teams.find(team => team.accessibilityItemProps.id == defaultTeam)?.accessibilityItemProps.answerValue ?? "";
        setSearchText(val);
        setButtonData(val);
        setButtonDataId(`${defaultTeam}`);
      }
      if(!done)
        setIsOpen(false);
    }

    const setCustomButtonIcon = (data: CustomButtonData) => {
      if(data.Button == CustomHelpCardConstants.Button_KnowledgeArticle && data.DataId.length > 0){
        const kb = kbListFullProps.find(kb => kb.id == parseInt(data.DataId));
        if(kb.answer.match(/^ApplyTemplate:\d+$/gi))
          data.ButtonIcon = CustomHelpCardConstants.Button_Template;
        else
          delete data.ButtonIcon;
      }

      return data;
    }

    const updateNestedNode = (editor, url) => {
      let paramUrl = JSON.parse(decodeURIComponent(url)) as CustomButtonData;
      for (const [node, path] of SlateNode.descendants(editor, {
        from: [],
      })) {
        // Check if the node is the one you want to update.
        if (node.type === 'link') {
          
          let nodeUrl = JSON.parse(decodeURIComponent(node["url"])) as CustomButtonData;
          if (paramUrl.ButtonId == nodeUrl.ButtonId){
            return path;
          }
        }
      }

      return [];
    };

    const onConfirmEdit = () => {
      
      editData.ScopingDetails = buttonScopedData;
      let data = clone(editData)
      data.DisplayName = buttonName;
      if(buttonData)
        data.Data = buttonData;
      else 
        delete data.Data;
      if(buttonDataId)
        data.DataId = buttonDataId;
      else
        delete data.DataId;
      data = setCustomButtonIcon(data);
      
      handleScopedData(editData, editor);

      const buttonLinks = document.querySelectorAll('#slate a[href^="%7B%22Button%22:"]');
      
      buttonLinks.forEach(link=>{
        let url = JSON.parse(decodeURIComponent(link.getAttribute("href"))) as CustomButtonData;
        if (url.ButtonId==editData.ButtonId)
        {
          data.IsScoped = data.ScopingDetails.ScopedMembers.length>0;
          delete data.ScopingDetails;
          let path = updateNestedNode(editor,encodeURI(JSON.stringify(data)));
          if (path) {
            Transforms.removeNodes(editor, { at: path });
            const divNode = {
              type: "link",
              url: encodeURI(JSON.stringify(data)),
              children: [{ text: data.DisplayName ?? editData.DisplayName }],
            };
            Transforms.insertNodes(editor, divNode as any,{at: path});
          }
        }
      });
      
      setOpenEditDialog(false);
    }

    const getSafeAt = (editor, sel) => {
      // Prefer an explicit selection passed in
      if (sel) {
        if (Range.isRange(sel)) return Editor.start(editor, sel);
        // selection-like object with path/offset from your code (BasePoint)
        if (sel.path && Array.isArray(sel.path)) return sel;
      }
      
      return {
                path: [0, 0],
                offset: 0,
              };
    };

    const isWholeParagraphSelected = (editor, sel) => {
      if (!sel || !Range.isRange(sel)) return false;

      const [block] = Array.from(Editor.nodes(editor, {
        at: sel,
        match: n => Editor.isBlock(editor, n),
      })) as unknown as Array<[any, Path]>;

      if (!block) return false;

      const [, path] = block;

      const blockRange = {
        anchor: Editor.start(editor, path),
        focus: Editor.end(editor, path),
      };

      return (
        Range.isExpanded(sel) &&
        Range.includes(sel, blockRange.anchor) &&
        Range.includes(sel, blockRange.focus)
      );
    }

    

    const insertButtonToken = () => { 
      let displayData = `{{Button: ${menuItems[selectedMenuIndex].value}, DisplayName: ${buttonName}`;
      let embedData: CustomButtonData = { Button: menuItems[selectedMenuIndex].value, ButtonId: Date.now().toString(), DisplayName: buttonName, ScopingDetails: buttonScopedData, IsScoped: buttonScopedData.ScopedMembers.length>0};
      if(buttonData?.length > 0){
        embedData.Data = buttonData;
        if(buttonDataId.length > 0)
          embedData.DataId = buttonDataId;
        displayData = `${displayData}, Data: ${buttonData}`;

        embedData = setCustomButtonIcon(embedData);
      }
      
      handleScopedData(embedData, editor);
      displayData = `${displayData}}}`;
      let _embedData = clone(embedData);
      delete _embedData.ScopingDetails;
      const embedDataStr = encodeURI(JSON.stringify(_embedData));
      let divNode:any;

      const atCandidate = getSafeAt(editor, slateSelection);
      const isAllSelectedLine = isWholeParagraphSelected(editor, slateSelection);

      // find the block containing the selection (safe)
      const aboveEntry = Editor.above(editor, {
        at: atCandidate,
        match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n),
      });
      let blockNode:any = aboveEntry ? aboveEntry[0] : null;
      let blockPath = aboveEntry ? aboveEntry[1] : (slateSelection?.path ?? [0]);

      // parsed embed for comparing ButtonId
      const embedParsed = (() => {
        try { return JSON.parse(decodeURIComponent(embedDataStr || '')); } catch { return null; }
      })();

      const docLength = editor?.children?.length ?? 0;

      // advance to the next block until we find one without scoped content
      // or reach end-of-doc (in which case we'll insert at the end)
      let countScopedTextWithSameId = 1;
      while (blockPath?.length && (blockPath[0] < docLength)) {
        // detect scoped content inside this block (element wrappers or scoped marks)
        const hasScoped = Array.from(
          Editor.nodes(editor, {
            at: blockPath,
            match: n =>
              (SlateElement.isElement(n) && ((n as any).type === scopeText || (n as any).type === 'scoped')) ||
              (SlateText.isText(n) && !!(n as any).scoped),
            mode: 'all'
          })
        ).length > 0;
        // move to next sibling block
        let next:Path = Path.next(blockPath);
        if (!hasScoped){
          if (countScopedTextWithSameId==1) break;
          
          next = Path.previous(blockPath);
        }

        countScopedTextWithSameId = countScopedTextWithSameId + 1;

        // if next is beyond doc, mark blockPath null to signal append-at-end
        if (!next || next[0] >= docLength) {
          blockNode = null;
          blockPath = null;
          break;
        }
        const found = Editor.node(editor, next);
        if (!found) {
          blockNode = null;
          blockPath = null;
          break;
        }
        blockNode = found[0];
        blockPath = found[1];

        if (!hasScoped) break;
      }

      // now decide whether to insert inline or create a new paragraph based on the target block
      const blockChildren = Array.isArray(blockNode?.children) ? blockNode.children : [];

      const blockHasOnlyEmptyText = blockChildren.some(x=>x.type=="link" && x["url"].startsWith("%7B%22Button%22:")) || (blockChildren.length === 1 && SlateText.isText(blockChildren[0]) && (blockChildren[0] as any).text === '');
      //if (isAllSelectedLine || match.children.length <=1 && (match.children[0] as any).text == "" || match.children.some(x=>x.type=="link" && x["url"].startsWith("%7B%22Button%22:")))
      let blockHasSimilarButton = false;
      if (blockNode && Array.isArray(blockNode.children)) {
        try {
          blockHasSimilarButton = blockNode.children.some((c: any) => {
            if (!SlateElement.isElement(c) || c.type !== 'link' || !c.url) return false;
            const parsed = JSON.parse(decodeURIComponent(c.url));
            return parsed?.ButtonId && embedParsed && parsed.ButtonId === embedParsed.ButtonId;
          });
        } catch { blockHasSimilarButton = false; }
      }

      // if user selected entire line, or block empty, or block already has similar button -> inline
      if (isAllSelectedLine || blockHasOnlyEmptyText || blockHasSimilarButton) {
        divNode = {
          type: "link",
          url: embedDataStr,
          children: [{ text: buttonName }],
        };
      } else {
        // otherwise create a new paragraph (ensures it's not inside scoped block)
        divNode = {
          type: "paragraph",
          children: [
            {
              type: "link",
              url: embedDataStr || "",
              children: [{ text: buttonName }],
            }
          ],
        };
      }

      // compute insertAt:
      let insertAt: Path | null = null;
      // find a link node in the target block whose url starts with the Button prefix
      const buttonLinkEntries = Array.from(
        Editor.nodes(editor, {
          at: blockPath,
          match: n => (SlateElement.isElement(n) && (n.type === 'link' && String(n.url || '').startsWith('%7B%22Button%22:')) || (SlateText.isText(n) && (n as any).text==='')),
          mode: 'all'
        })
      ) as Array<[any, Path]>;

      if (buttonLinkEntries.length > 0) {
        // choose the last matching link in the block and insert after it (beside it)
        const [, linkPath] = buttonLinkEntries[buttonLinkEntries.length - 1];
        insertAt = Path.next(linkPath);
      }

      if (!insertAt) {
        if (lastButtonPath) {
          insertAt = Path.next(lastButtonPath);
        } else if (divNode.type === 'link') {
          // inline insertion: prefer selection.path, fallback to blockPath or doc end
          insertAt = slateSelection?.path ?? blockPath ?? [docLength];
        } else {
          // paragraph insertion: insert after the target block (or at doc end)
          insertAt = blockPath ? Path.next(blockPath) : [docLength];
        }
      }

      // perform insertion
      if (insertAt) {
        Transforms.insertNodes(editor, divNode, { at: insertAt });
      } else {
        Transforms.insertNodes(editor, divNode, { at: slateSelection || Editor.end(editor, []) });
      }

      // Find the path of the newly inserted node and update lastButtonPath
      const [newNodeEntry] = Editor.nodes(editor, {
        at: [],
        match: n => SlateElement.isElement(n) && n.type === 'link' && n.url === embedDataStr,
        mode: 'all'
      });

      if (newNodeEntry) {
        const [, newPath] = newNodeEntry;
        setLastButtonPath(newPath);

        const nextPath = Path.next(newPath);

        try {
          SlateNode.get(editor, nextPath);
        } catch {
          Transforms.insertNodes(
            editor,
            { text: "" },
            { at: nextPath }
          );
        }

        Transforms.select(editor, {
          path: nextPath,
          offset: 0
        });
      }
    };

    const onCloseDialog = () => {
      setOpenEditDialog(false);
      setIsOpen(false);
      setButtonName("");
      setButtonData("");
      setButtonDataId("");
      setSearchText("");
    }
    const closeScopedWindow = (selectedTextId, noHistory) => {
      removeTextScoped({id: selectedTextId},noHistory);

      setIsNewScopedText(false);
      setOpenScopedWindow(false);
    }

    const editTextScoped = (e) => {
      let selectedid = 0;

      if (e.id!=undefined) selectedid = e.id;
      else
      {
        let target = e.target;
        if(target.nodeName.toString() == "SPAN"){
          selectedid = target.getAttribute("selectedId");
        }
        else
        {
          const parent = target.closest(".editCustomButton");
          if(parent)
            selectedid = parent.getAttribute("selectedId");
        }
      }

      if (selectedid==0) return;

      setEditData({ButtonId:selectedid.toString(),Button:"",DisplayName:"", IsScoped:false});

      setNewSelectedTextId(selectedid);
      setIsEdit(true);
      setOpenScopedWindow(true);
    }
    const resetEditor = () => {
      Transforms.delete(editor, { at: Editor.range(editor, []) });
      Transforms.insertNodes(editor, markdownToSlate(minifyHtmlString(CustomHelpCardConstants.DefaultEndUserCardBody)),{at: [0]});
    }

    const [lastActiveSelection, setLastActiveSelection] = useState<any>(null)
    const insertImage = (editor, url, width, height) => {

        let style: React.CSSProperties = {
          display: 'block',
          width: width ? `${width}px` : "auto",
          height: height ? `${height}px` : "auto",
          margin: '0', // Default center
          marginLeft: 'auto', marginRight: 'auto', float: 'none'
        };

      const text = { text: '' };
      const image = { type: 'image', url, style, children: [text] } as any;
      Transforms.insertNodes(editor, image, {
        at: lastActiveSelection || Editor.end(editor, []),
      });
      setOpenImgTool(false)
    };
    

    let imgurl = "";
    let imgH = "";
    let imgW = "";
    const imageToolBar = () => {

      return <Popup
        open={openImgTool}
        content={
          
          <div className={`${globalTheme.siteVariables.theme !== themeNames.Default && 'dark-mood-custom'}`} >
            <div className="rdw-image-modal-header"><span className="rdw-image-modal-header-option">URL<span className="rdw-image-modal-header-label rdw-image-modal-header-label-highlighted"></span></span></div>
            <div className="rdw-image-modal-url-section">
              <Input
                  fluid
                  placeholder={"Image URL..."}
                  onChange={(_e, p) => {
                    imgurl = p.value;
                  }}
                  className="mb-2"
                /><span className="rdw-image-mandatory-sign">*</span>
            </div>
            <div className="rdw-image-modal-size">
              ↕&nbsp;<Input
                  fluid
                  defaultValue={"auto"}
                  onChange={(_e, p) => {
                    imgH = p.value;
                  }}
                  className="mb-2"
                /><span className="rdw-image-mandatory-sign">*</span>
                  &nbsp;↔&nbsp;<Input
                      fluid
                      defaultValue={"auto"}
                      onChange={(_e, p) => {
                        imgW = p.value;
                      }}
                      className="mb-2"
                    /><span className="rdw-image-mandatory-sign">*</span>
            </div>
            <span className={`${globalTheme.siteVariables.theme !== themeNames.Default && 'dark-mood-custom'}`}>
              <button className="rdw-image-modal-btn slate-rdw-image-modal-btn" onClick={()=>{insertImage(editor,imgurl,imgW, imgH)}}>Add</button>
              <button className="rdw-image-modal-btn slate-rdw-image-modal-btn" onClick={()=>{setOpenImgTool(false)}}>Cancel</button>
            </span>
          </div>
          }
        trigger={
        <div className='rdw-option-wrapper slate-inser-img'>
          <img onClick={()=>{setOpenImgTool(true)}} src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUiIGhlaWdodD0iMTQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0iIzAwMCIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTQuNzQxIDBILjI2Qy4xMTYgMCAwIC4xMzYgMCAuMzA0djEzLjM5MmMwIC4xNjguMTE2LjMwNC4yNTkuMzA0SDE0Ljc0Yy4xNDMgMCAuMjU5LS4xMzYuMjU5LS4zMDRWLjMwNEMxNSAuMTM2IDE0Ljg4NCAwIDE0Ljc0MSAwem0tLjI1OCAxMy4zOTFILjUxN1YuNjFoMTMuOTY2VjEzLjM5eiIvPjxwYXRoIGQ9Ik00LjEzOCA2LjczOGMuNzk0IDAgMS40NC0uNzYgMS40NC0xLjY5NXMtLjY0Ni0xLjY5NS0xLjQ0LTEuNjk1Yy0uNzk0IDAtMS40NC43Ni0xLjQ0IDEuNjk1IDAgLjkzNC42NDYgMS42OTUgMS40NCAxLjY5NXptMC0yLjc4MWMuNTA5IDAgLjkyMy40ODcuOTIzIDEuMDg2IDAgLjU5OC0uNDE0IDEuMDg2LS45MjMgMS4wODYtLjUwOSAwLS45MjMtLjQ4Ny0uOTIzLTEuMDg2IDAtLjU5OS40MTQtMS4wODYuOTIzLTEuMDg2ek0xLjgxIDEyLjE3NGMuMDYgMCAuMTIyLS4wMjUuMTcxLS4wNzZMNi4yIDcuNzI4bDIuNjY0IDMuMTM0YS4yMzIuMjMyIDAgMCAwIC4zNjYgMCAuMzQzLjM0MyAwIDAgMCAwLS40M0w3Ljk4NyA4Ljk2OWwyLjM3NC0zLjA2IDIuOTEyIDMuMTQyYy4xMDYuMTEzLjI3LjEwNS4zNjYtLjAyYS4zNDMuMzQzIDAgMCAwLS4wMTYtLjQzbC0zLjEwNC0zLjM0N2EuMjQ0LjI0NCAwIDAgMC0uMTg2LS4wOC4yNDUuMjQ1IDAgMCAwLS4xOC4xTDcuNjIyIDguNTM3IDYuMzk0IDcuMDk0YS4yMzIuMjMyIDAgMCAwLS4zNTQtLjAxM2wtNC40IDQuNTZhLjM0My4zNDMgMCAwIDAtLjAyNC40My4yNDMuMjQzIDAgMCAwIC4xOTQuMTAzeiIvPjwvZz48L3N2Zz4=" alt=""></img>
        </div>}
        
      />;
    }

    const removeTextScoped = (e, noHistory) => {
      let selectedid = e.id ?? e.target.getAttribute("selectedId");
      if (!selectedid) return;

      // normalize id to string for comparison
      const targetIdStr = String(selectedid);

      const doUnwrap = () => {
        // 1) Remove mark-based scoped entries on text leaves
        const markedTextEntries = Array.from(
          Editor.nodes(editor, {
            at: [],
            match: n => SlateText.isText(n) && !!(n as any).scoped && String((n as any).scoped?.id) === targetIdStr,
            mode: 'all'
          })
        ) as unknown as Array<[any, Path]>;
        let ctr = 0;
        if (markedTextEntries.length > 0) {
          // deepest-first (not strictly required for text leaves but safe)
          markedTextEntries.sort((a, b) => Path.compare(b[1], a[1]));
          for (const [, path] of markedTextEntries) {
            // prefer unsetNodes if available, otherwise set scoped to undefined
           if ((Transforms as any).unsetNodes) {
              Transforms.unsetNodes(editor, 'scoped', { at: path, match: n => SlateText.isText(n), split: false });
            } else {
              Transforms.setNodes(editor, { scoped: undefined } as any, { at: path, match: n => SlateText.isText(n), split: false });
            }
          }
        }
      };

      if (noHistory) {
        HistoryEditor.withoutSaving(editor, () => {
          HistoryEditor.undo(editor);
        });
      } else {
        doUnwrap();
      }
    }

    const applyTextScoped = () => {
      const data:CustomButtonData = {
        Button:"",
        DisplayName:"",
        IsScoped:buttonScopedData.ScopedMembers.length>0,
        ButtonId: newSelectedTextId.toString(),
        ScopingDetails: buttonScopedData
      };

      if (buttonScopedData.ScopedMembers.length==0) 
        removeTextScoped({id: newSelectedTextId}, false);

      
      handleScopedData(data,editor);
      setIsNewScopedText(false);
      closeScopedWindow(0, false);
      
    }

    const getAllHighlightedScopedIds = (editor, sel = editor?.selection) => {
      if (!editor || !sel) return [] as string[];
      const range = Range.isRange(sel) ? sel : Editor.range(editor, sel);
      if (!range || Range.isCollapsed(range)) return [] as string[];

      const ids = new Set<string>();

      for (const [node] of Editor.nodes(editor, { at: range, mode: 'all' })) {
        // mark-based scoped (leaf.scoped)
        if (SlateText.isText(node) && (node as any).scoped?.id) {
          ids.add(String((node as any).scoped.id));
          continue;
        }
        // element-based legacy scoped wrappers
        if (SlateElement.isElement(node) && ((node as any).type === scopeText || (node as any).type === 'scoped') && (node as any).id) {
          ids.add(String((node as any).id));
        }
      }

      return Array.from(ids);
    };

    const ScopedTextButton = () => {


      if (!enabledScoping){
        return <></>;
      }

      const handleClick = (event) => {
        event.preventDefault();
        setIsNewScopedText(true);

        try {
          const sel = editor?.selection ?? slateSelection;
          if (!sel || Range.isCollapsed(sel)) {
            scopingDialogAlert(t("scoping.select-text-to-scoped"));
            return;
          }

          let range = Range.isRange(sel) ? sel : Editor.range(editor, sel);

          // Normalize selection direction.
          // Makes bottom->top behave exactly like top->bottom.
          range = Editor.range(
            editor,
            Range.start(range),
            Range.end(range)
          );

          /**
           * Triple-click fix
           */
          const startPoint = Range.start(range);
          const endPoint = Range.end(range);

          const startBlock = Editor.above(editor, {
            at: startPoint,
            match: n => Editor.isBlock(editor, n),
          });

          const endBlock = Editor.above(editor, {
            at: endPoint,
            match: n => Editor.isBlock(editor, n),
          });

          if (
            startBlock &&
            endBlock &&
            !Path.equals(startBlock[1], endBlock[1]) &&
            Editor.isStart(editor, endPoint, endBlock[1])
          ) {
            range = {
              anchor: Editor.start(editor, startBlock[1]),
              focus: Editor.end(editor, startBlock[1]),
            };
          }

          /**
           * Find first button link in selection
           */
          const [firstLinkEntry] = Editor.nodes(editor, {
            at: range,
            match: (n: any) =>
              n.type === "link" &&
              n.url?.includes("Button%22:"),
            mode: "lowest",
          });

          if (firstLinkEntry) {
            const [, linkPath] = firstLinkEntry;

            const linkBlock = Editor.above(editor, {
              at: linkPath,
              match: n => Editor.isBlock(editor, n),
            });

            if (linkBlock) {
              const [, linkBlockPath] = linkBlock;

              try {
                const previousBlockPath = Path.previous(linkBlockPath);
                const endPoint = Editor.end(editor, previousBlockPath);

                range = {
                  anchor: range.anchor,
                  focus: endPoint,
                };
              } catch {
                // Button is in the first block, nothing before it.
              }
            }
          }

          getAllHighlightedScopedIds(editor,range).forEach(id => {
                    removeTextScoped({id}, false);
                  });

          /**
           * Recalculate blocks AFTER all normalization
           */
          const blockEntries = Array.from(
            Editor.nodes(editor, {
              at: range,
              match: n => Editor.isBlock(editor, n),
              mode: "lowest",
            })
          ) as Array<[any, Path]>;

          const textBlocks = blockEntries.filter(([node]) => {
            const text = SlateNode.string(node).trim();
            return text.length > 0;
          });
          const linesCount = textBlocks.length;
          if (blockEntries.length === 0) {
            scopingDialogAlert(t("scoping.selected-text-invalid"));
            return;
          }

          const selectionId = Date.now().toString();
          setNewSelectedTextId(Number(selectionId));

          // process blocks in document order; for each block wrap only the selected parts inside that block
          const sortedBlocks = textBlocks.sort((a, b) =>
            Path.compare(a[1], b[1])
          );
          for (let lineIndex = 0; lineIndex < sortedBlocks.length; lineIndex++) {
            const [, blockPath] = sortedBlocks[lineIndex];

            // intersection between whole selection and this block
            const blockRange = { anchor: Editor.start(editor, blockPath), focus: Editor.end(editor, blockPath) };
            const intersectBlock = Range.intersection(range, blockRange);
            if (!intersectBlock) continue;

            // find text leaves inside that intersecting block-range (document order), then wrap deepest-first
            const leaves = Array.from(
              Editor.nodes(editor, { at: intersectBlock, match: n => SlateText.isText(n), mode: 'all' })
            ) as unknown as Array<[any, Path]>;
            if (leaves.length === 0) continue;

            const leavesDesc = leaves
              .map(e => ({ node: e[0], path: e[1] }))
              .sort((a, b) => Path.compare(b.path, a.path)); // deepest-first

            for (const { node, path } of leavesDesc) {
              // compute intersection of selection with this leaf
              const leafFullRange = { anchor: { path, offset: 0 }, focus: { path, offset: (typeof node.text === 'string' ? node.text.length : 0) } };
              const intersectLeaf = Range.intersection(intersectBlock, leafFullRange);
              if (!intersectLeaf) continue;

              Transforms.setNodes(
                editor,
                {
                  scoped: {
                    id: selectionId,
                    indexid: String(lineIndex),
                    count: String(linesCount)
                  }
                } as any,
                { at: intersectLeaf, match: n => SlateText.isText(n), split: true }
              );
            }

          }


          setIsEdit(false);
          setOpenScopedWindow(true);
        } catch (ex) {
          console.error(ex);
          scopingDialogAlert(t("scoping.selected-text-invalid"));
        }
      };

      return (
        <div className="ui-menubutton rdw-inline-wrapper" style={{ boxSizing: 'border-box', display: 'inline-block' }}>
          <div onMouseDown={handleClick} className="scope-btn">
            {globalTheme.siteVariables.theme === themeNames.Dark ? <ScopeButtonDarkSVG /> : <ScopeButtonLightSVG />}
          </div>
        </div>
      );
    };

  const normalizeScopedElementsToMarks = (editor) => {
    if (!editor) return;
    try {
      // Process scoped wrappers repeatedly, always re-querying current document state.
      // This avoids stale paths / path invalidation when previous transforms mutate the tree.
      while (true) {
        const scopedMatches = Array.from(
          Editor.nodes(editor, {
            at: [],
            match: n => SlateElement.isElement(n) && (n.type === scopeText || n.type === 'scoped'),
            mode: 'all'
          })
        ) as unknown as Array<[any, Path]>;

        if (scopedMatches.length === 0) break;

        // pick the deepest occurrence (largest path) first
        scopedMatches.sort((a, b) => Path.compare(b[1], a[1]));
        const [node, elPath] = scopedMatches[0];

        const id = String(node.id ?? node.props?.id ?? node.attributes?.id ?? '');
        const meta = {
          id,
          indexid: String(node.indexid ?? node.props?.indexid ?? 0),
          count: String(node.count ?? node.props?.count ?? 0)
        };

        // Try to set scoped mark for all text leaves under this element in one bulk operation
        try {
          Transforms.setNodes(editor, { scoped: meta } as any, { at: elPath, match: n => SlateText.isText(n), split: true });
        } catch (bulkErr) {
          // Fallback: set per-leaf deepest-first (best-effort)
          try {
            const textLeaves = Array.from(
              Editor.nodes(editor, { at: elPath, match: n => SlateText.isText(n), mode: 'all' })
            ) as unknown as Array<[any, Path]>;
            textLeaves.sort((a, b) => Path.compare(b[1], a[1]));
            for (const [, p] of textLeaves) {
              try {
                Transforms.setNodes(editor, { scoped: meta } as any, { at: p, match: n => SlateText.isText(n), split: true });
              } catch { /* ignore individual failures */ }
            }
          } catch { /* ignore */ }
        }

        // Unwrap the element wrapper by matching id/type (best-effort)
        try {
          Transforms.unwrapNodes(editor, {
            at: elPath,
            match: n => SlateElement.isElement(n) && (n.type === scopeText || n.type === 'scoped') && String((n as any).id ?? '') === id,
            mode: 'lowest'
          });
        } catch (unwrapErr) {
          // ignore and continue; next loop will re-query and either find nothing or handle remaining nodes
        }

        // loop continues and re-queries the document, avoiding stale paths
      }
    } catch (err) {
      console.error('normalizeScopedElementsToMarks error', err);
    }
  };

  // run conversion after editor is created / content loaded
  useEffect(() => {
    if (!editor) return;
    // call after microtask so editor.children are populated by deserialization
    Promise.resolve().then(() => {
      try {
        if (typeof normalizeScopedElementsToMarks === "function") {
          normalizeScopedElementsToMarks(editor);
        }
      } catch (err) {
        console.error('normalize on load failed', err);
      }
      // Bot Configuration keeps Save disabled until the editor has reported once, so report on load rather than wait for the user. #35405
      onChangeEditorComponent(editor.children, editor);
    });
  }, [editor, loaded]);

    const [open, setOpen] = useState(false);
    const [alertDialogText, setAlertDialogText] = useState(false);
    const scopingDialogAlert = (scopedText) => {
      setAlertDialogText(scopedText);
      setOpen(true);
    }

    const cleanupSecurityScope = () => {
      //This will remove the security scope if scope id is not found from the db.
      const html = props.helpcardProps.htmlString.replace(/<\/a>(?=<|$)/g, '</a> ').replaceAll('&nbsp;</', '</');
      if (scopedButtons.length > 0)
      {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const textScope = doc.querySelectorAll('scoped');
        for (const scope of textScope)
        {
          if (!scopedButtons.some(x=>x.ButtonId.toString()==scope.id)) 
            {
              scope.replaceWith(...scope.childNodes);
            }
        }

        const btnScoped = doc.querySelectorAll('a[href^="%7B%22Button%22:"]');
        for (const scope of btnScoped)
        {
          const hrefValue = scope.getAttribute("href").toString();
          const btnValue = JSON.parse(decodeURI(hrefValue));
          if (!enabledScoping || (btnValue.IsScoped && !scopedButtons.some(x=>x.ButtonId.toString()==btnValue.ButtonId))) 
          {
            btnValue.IsScoped=false;
            scope.setAttribute("href",encodeURI(JSON.stringify(btnValue)));
          }
        }

        return doc.body.innerHTML;
      }
      else return html;
      
    }

    const disableSave = () => {

      if (openScopedWindow)
      {
        return ((!isEdit && !scopingEnabled) || (scopingEnabled && buttonScopedData.ScopedMembers.length==0));
      }
      else {
        const selectedMenu = menuItems[selectedMenuIndex];
        let isDisabled = false;
        switch(selectedMenu.key){
          case CustomHelpCardConstants.Button_Template:
          case CustomHelpCardConstants.Button_KnowledgeArticle:
              isDisabled = buttonName.trim() == "" || buttonData.trim() == "";
              break;
          case CustomHelpCardConstants.Button_OpenURL:
            isDisabled = buttonName.trim() == "" || buttonData.trim() == "" || !isValidURL(buttonData);
            break;
          default:
            isDisabled = buttonName.trim() == "";
            break;
        }
        
        return isDisabled || (scopingEnabled && buttonScopedData.ScopedMembers.length==0);
      }
    }

    const onChangeEditorComponent = (data: any, _editor:any) => { setTimeout(() => { onChangeShowEditorCallback(data, _editor, setEditorIsLoaded, editorIsLoaded); }, 10); };
    const onChangeEditorComponentCallback = useCallback((data: any, _editor:any) => { onChangeEditorComponent(data, _editor); }, [onChangeEditorComponent, props.helpcardProps]);
    
    const htmlString = cleanupSecurityScope();
    return <>
      <div id="slate"  className={`${globalTheme.siteVariables.theme !== themeNames.Default && 'dark-mood-custom'}`} style={{ position: 'relative', paddingTop:"1px", color: 'var(--mgt-theme-foreground)', backgroundColor: 'var(--mgt-theme-background)', ...ThemeColorScheme(globalTheme.siteVariables) }}>
        {loaded &&
          <SlateEditor
            noHeaders={true}
            onChange={onChangeEditorComponentCallback}
            onKeyDown={handleEditorKeyDown}
            slateValue={markdownToSlate(htmlString)}
            scopedButtons={scopedButtons}
            readOnly={false}
            usersInMention={[]}
            onBlur={()=>{setLastActiveSelection({...editor.selection});}}
            isFocus={false}
            noImage={true}
            verticalResize={true}
            customToolbar={[imageToolBar(),
                          ScopedTextButton(),
                          <MenuButton
                            onMenuItemClick={onMenuChange}
                            trigger={<Button content={CustomHelpCardConstants.Add_Button} onClick={()=>{
                              const {selection} = editor;
                              if (selection!=null){
                                setSlateSelection(selection);
                                setLastButtonPath(selection.path); // Reset lastButtonPath when user manually selects text
                              }
                              
                            }} icon={<div style={{width: '20px'}}><AddButtonSVG fill={globalTheme.siteVariables.colorScheme.default.foreground} /></div>} iconPosition="before" style={{padding: '0 0.5em'}} className='ml-2' />}
                            menu={menuItems.filter(x=>x.key!=CustomHelpCardConstants.Scoped_Text)}
                            className='rdw-inline-wrapper'
                          />, 
                          <Dialog key={"set-default"} closeOnOutsideClick={false}
                            trigger={<div className="ui-menubutton rdw-inline-wrapper" style={{boxSizing: 'border-box', display: 'inline-block'}}><Button content={CustomHelpCardConstants.ResetDefault} icon={<div style={{width: '20px'}}><ResetDefaultSVG fill={globalTheme.siteVariables.colorScheme.default.foreground} /></div>} iconPosition="before" style={{padding: '0 0.5em'}} className='ml-2'/></div>}
                            onConfirm={resetEditor}
                            cancelButton={toTitleCase(CustomHelpCardConstants.ResetNo)}
                            confirmButton={toTitleCase(CustomHelpCardConstants.ResetYes)}
                            content={<>
                              <Flex className='my-2'><Text content={CustomHelpCardConstants.ResetDefaultMessage}  /></Flex>
                            </>}
                            header={CustomHelpCardConstants.ResetDefault} 
                            style={{ maxWidth: '500px' }}
                          />
                        ]}
            editor={(e)=>{
              editor = e;
            }}
            minHeight='480px'
            helpcardProps={props}
          />
        }
        
        
      </div>
      <Dialog
        closeOnOutsideClick={false}
        trapFocus={false}
        open={isOpen || openScopedWindow}
        footer={
          <Flex gap="gap.small" className="mt-5">
              <Flex.Item push>
              <Button content={toTitleCase(BUTTONS.CANCEL)} 
                onClick={() => { 
                  if (openScopedWindow && !isEdit)
                  {
                    closeScopedWindow(newSelectedTextId, isNewScopedText);
                  }
                  else
                  {
                    closeScopedWindow(0, isNewScopedText);
                    onCloseDialog() 
                  }
                  
                }}/>
              </Flex.Item>
              <Button content={toTitleCase(BUTTONS.OK)} primary 
                disabled={disableSave()}
                onClick={() => {
                  if (openScopedWindow)
                  {
                    setSelectedMenuIndex(5);
                    applyTextScoped();
                  }
                  else
                  {
                    if(openEditDialog)
                      onConfirmEdit();
                    else
                      insertButtonToken();

                    onCloseDialog();
                  }
                   
                }} />
          </Flex>
        }
        content={<Flex column gap={"gap.medium"}>{getDialogContent(openScopedWindow)}</Flex>}
        header={isLoading ? <></> : <>{`${openEditDialog || openScopedWindow ? CustomHelpCardConstants.Edit : CustomHelpCardConstants.Add} ${menuItems[selectedMenuIndex].content} ${openEditDialog ? CustomHelpCardConstants.Button : ""}`}</>}
        style={{ maxWidth: '500px' }}
      />
      <Dialog
          content={
            <>
              <div>
                {alertDialogText}
              </div>
            </>
          }
          open={open}
          onOpen={() => setOpen(true)}
          header="Info"
          cancelButton="Ok"
          onCancel={() => setOpen(false)}
          style={{ maxWidth: '360px', width: '360px', padding: '12px' }}
          variables={{ modalMaxWidth: '360px' }}
        />
    </>
  }